/* eslint-disable ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { findProhibitedWords } from '@linc-technologies/linced-common/utils/financial-language';
import { tagName } from '@ember-decorators/component';

// ProhibitedLanguageAlert validates a single free-text field against the
// school's prohibited financial language list (SMOS-012) and displays a
// warning when restricted terms are found.
//
// Usage:
//   <Finance::ProhibitedLanguageAlert
//     @text={{fieldValue}}
//     @onChange={{action (mut violationsProperty)}}
//   />
//
// @text     — the current value of the field being validated
// @onChange — action called with the violations array whenever it changes,
//             allowing the parent controller to store violations and use them
//             to gate save buttons
//
// The component reads the school's word list and jurisdiction eligibility
// directly from the session service, so callers don't need to pass them.
@tagName('')
export default class ProhibitedLanguageAlert extends Component {
  @service
  session;

  @tracked text = null;

  // initialText is the system-provided default value for the field, if any.
  // Prohibited words already present in initialText are excluded from
  // violations so that system-introduced language (e.g. pre-populated email
  // templates) is not flagged until the user has actually added a new
  // prohibited word themselves.
  @tracked initialText = null;

  onChange = null;

  // violations is the list of prohibited words found in @text that were not
  // already present in @initialText.
  @computed(
    'text',
    'initialText',
    'session.school.{requiresFinancialLanguageValidation,nswDoeProhibitedFinancialLanguage}',
  )
  get violations() {
    if (
      !this.session.school ||
      !this.session.school.requiresFinancialLanguageValidation
    )
      return [];
    const wordList = this.session.school.nswDoeProhibitedFinancialLanguage;
    const all = findProhibitedWords(this.text, wordList);
    if (isEmpty(this.initialText)) return all;
    const preExisting = new Set(
      findProhibitedWords(this.initialText, wordList),
    );
    return all.filter((word) => !preExisting.has(word));
  }

  // violationsText is the human-readable, comma-separated form shown in the
  // warning message body.
  @computed('violations')
  get violationsText() {
    return isEmpty(this.violations) ? '' : this.violations.join(', ');
  }

  // Notify the parent controller whenever attrs change so it can update its
  // stored violations property and gate save buttons accordingly.
  // Safe from infinite loops because the parent's violations property is never
  // passed back into this component as an arg, so setting it via onChange
  // does not trigger another didReceiveAttrs cycle.
  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    if (this.onChange) {
      this.onChange(this.violations);
    }
  }
}
